import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { hasWebSession } from 'src/app/commons/utils/web-session';
import { AppConstants } from 'src/app/commons/app-constants';
import { SHARED_MODULES } from 'src/app/modules/shared-imports';
import { ToastMessagesService } from 'src/app/commons/services/toast-messages.service';
import { TranslationService } from 'src/app/i18n/translation.service';
import { PushNotificationService } from 'src/app/services/push-notification.service';

/** An approval request: someone on a WhatsApp number asks LumberLinq's assistant for help on THIS user's account. */
interface WaPendingApproval {
  id: number;
  /** e.g. ••••5679 (never the full number). */
  maskedNumber: string;
  requestedAt: string;
}

/**
 * "Approve WhatsApp access?" — the in-app half of customer verification. When a customer on WhatsApp gives this user's
 * registered email, the server sends this user a push and raises a request. This sheet shows it; tapping Approve inside
 * the user's own logged-in session is what proves it is them (nothing to type, nothing to forward). Deny blocks that
 * number for an hour.
 *
 * Looks for waiting requests when the app opens, when it comes back to the front, and when a matching push arrives while
 * it is open — no timers. Sits in the app shell, so it only exists for a signed-in user.
 */
@Component({
  selector: 'app-wa-approval',
  standalone: true,
  imports: [CommonModule, SHARED_MODULES],
  template: `
    <div class="wap-overlay" *ngIf="current" (click)="$event.stopPropagation()">
      <section class="wap-sheet" role="alertdialog" aria-modal="true" [attr.aria-label]="'crmverify.title' | t">
        <div class="wap-grabber" aria-hidden="true"></div>
        <div class="wap-badge"><i class="pi pi-whatsapp"></i></div>
        <h3 class="wap-title">{{ 'crmverify.title' | t }}</h3>
        <p class="wap-text">{{ 'crmverify.text' | t }}</p>
        <div class="wap-number"><i class="pi pi-mobile"></i> {{ current.maskedNumber }}</div>
        <p class="wap-hint">{{ 'crmverify.hint' | t }}</p>
        <p class="wap-more" *ngIf="queue.length > 1">{{ queue.length - 1 }} {{ 'crmverify.more' | t }}</p>
        <div class="wap-actions">
          <button type="button" class="wap-btn wap-btn--deny" (click)="decide(false)" [disabled]="busy">
            <i class="pi pi-times"></i> {{ 'crmverify.deny' | t }}
          </button>
          <button type="button" class="wap-btn wap-btn--ok" (click)="decide(true)" [disabled]="busy">
            <i class="pi" [ngClass]="busy ? 'pi-spin pi-spinner' : 'pi-check'"></i> {{ 'crmverify.approve' | t }}
          </button>
        </div>
      </section>
    </div>
  `,
  styles: [`
    @keyframes wapFade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes wapRise { from { opacity: 0; transform: translateY(24px) scale(0.985); } to { opacity: 1; transform: none; } }
    @keyframes wapSlide { from { transform: translateY(100%); } to { transform: none; } }
    .wap-overlay { position: fixed; inset: 0; z-index: 1095; display: flex; align-items: center; justify-content: center; padding: 1rem;
      background: rgba(15, 23, 42, 0.5); -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px); animation: wapFade 0.2s ease both; }
    .wap-sheet { position: relative; width: min(420px, 100%); text-align: center; padding: 1.6rem 1.4rem 1.3rem; border-radius: 24px;
      background: var(--surface-card); color: var(--text-color); border: 1px solid var(--surface-border);
      box-shadow: 0 30px 80px rgba(2, 6, 23, 0.4); animation: wapRise 0.3s cubic-bezier(0.22, 1, 0.36, 1) both; }
    .wap-grabber { display: none; width: 2.6rem; height: 0.3rem; border-radius: 100px; background: var(--surface-border); margin: 0 auto 0.9rem; }
    .wap-badge { width: 3.4rem; height: 3.4rem; margin: 0 auto 0.8rem; border-radius: 18px; display: flex; align-items: center; justify-content: center;
      font-size: 1.6rem; color: #fff; background: linear-gradient(135deg, #25d366, #128c7e); box-shadow: 0 8px 22px rgba(37, 211, 102, 0.4); }
    .wap-title { margin: 0 0 0.4rem; font-size: 1.15rem; font-weight: 700; letter-spacing: -0.01em; }
    .wap-text { margin: 0 0 0.8rem; font-size: 0.9rem; line-height: 1.5; color: var(--text-color-secondary); }
    .wap-number { display: inline-flex; align-items: center; gap: 0.45rem; padding: 0.45rem 1rem; border-radius: 100px; font-size: 1.05rem; font-weight: 700;
      letter-spacing: 0.04em; font-variant-numeric: tabular-nums; background: var(--surface-ground); border: 1px solid var(--surface-border); }
    .wap-number i { color: #128c7e; font-size: 0.9rem; }
    .wap-hint { margin: 0.9rem 0 0; font-size: 0.78rem; line-height: 1.45; color: var(--text-color-secondary); }
    .wap-more { margin: 0.5rem 0 0; font-size: 0.74rem; font-weight: 600; color: #1684ac; }
    .wap-actions { display: flex; gap: 0.6rem; margin-top: 1.2rem; }
    .wap-btn { flex: 1; min-height: 3rem; border: 0; border-radius: 14px; cursor: pointer; font: inherit; font-size: 0.95rem; font-weight: 700;
      display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; transition: transform 0.15s, box-shadow 0.15s, opacity 0.15s; }
    .wap-btn:active:not(:disabled) { transform: scale(0.98); }
    .wap-btn:disabled { opacity: 0.6; cursor: default; }
    .wap-btn--deny { color: #dc2626; background: var(--surface-ground); border: 1px solid var(--surface-border); }
    .wap-btn--ok { color: #fff; background: linear-gradient(135deg, #25d366, #128c7e); box-shadow: 0 8px 22px rgba(37, 211, 102, 0.4); }
    @media (max-width: 640px) {
      .wap-overlay { align-items: flex-end; padding: 0; }
      .wap-sheet { width: 100%; border-radius: 26px 26px 0 0; border-bottom: 0; padding-bottom: calc(1.3rem + env(safe-area-inset-bottom));
        animation: wapSlide 0.34s cubic-bezier(0.22, 1, 0.36, 1) both; }
      .wap-grabber { display: block; }
    }
    @media (prefers-reduced-motion: reduce) { .wap-overlay, .wap-sheet { animation: none; } .wap-btn { transition: none; } }
  `],
})
export class WaApprovalComponent implements OnInit, OnDestroy {

  queue: WaPendingApproval[] = [];
  busy = false;

  private readonly base = `${AppConstants.API_URL}api/v1/wa-verify`;
  private sub?: Subscription;
  private lastLook = 0;

  constructor(private http: HttpClient, private toast: ToastMessagesService, private i18n: TranslationService,
              private push: PushNotificationService) {}

  get current(): WaPendingApproval | null { return this.queue.length ? this.queue[0] : null; }

  ngOnInit(): void {
    this.look(true);
    // a push about a request arrived while the app is open
    this.sub = this.push.foregroundPush.subscribe((p) => {
      if (p?.data && (p.data as Record<string, string>)['type'] === 'WA_VERIFY') this.look(true);
    });
  }

  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  /** The app came back to the front (a tapped push opens it here): look for waiting requests. */
  @HostListener('document:visibilitychange')
  onVisible(): void {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') this.look(false);
  }

  private look(force: boolean): void {
    const now = Date.now();
    if (!force && now - this.lastLook < 8000) return; // at most one look per 8 seconds
    // nobody signed in (login, sign-up, Check Your Email pages): nothing to ask, and the answer could only be 401
    if (!hasWebSession()) return;
    this.lastLook = now;
    this.http.get<WaPendingApproval[]>(`${this.base}/pending`, { withCredentials: true }).subscribe({
      next: (list) => { this.queue = list || []; },
      error: () => { /* signed out or offline: nothing to show */ },
    });
  }

  decide(approve: boolean): void {
    const c = this.current;
    if (!c || this.busy) return;
    this.busy = true;
    this.http.post<void>(`${this.base}/${c.id}/${approve ? 'approve' : 'deny'}`, {}, { withCredentials: true }).subscribe({
      next: () => {
        this.busy = false;
        this.queue = this.queue.filter((x) => x.id !== c.id);
        this.toast.showSuccessMessage(this.i18n.instant(approve ? 'crmverify.approvedTitle' : 'crmverify.deniedTitle'),
          this.i18n.instant(approve ? 'crmverify.approvedSub' : 'crmverify.deniedSub'));
      },
      error: (e) => {
        this.busy = false;
        this.queue = this.queue.filter((x) => x.id !== c.id); // expired or already handled: drop it
        this.toast.showErrorMessage(this.i18n.instant('crmverify.title'), e?.error?.message || this.i18n.instant('crmverify.expired'));
      },
    });
  }
}
